'use client';
import React, { useRef, useState } from 'react';
import { ImageIcon, Loader2 } from 'lucide-react';
import { uploadImage } from '@/lib/supabase/services';
import AppImage from './AppImage';

interface ImageUploadProps {
  bucket: 'id-cards' | 'materials' | 'products';
  currentUrl?: string;
  onUpload: (url: string) => void;
  label?: string;
  folder?: string;
}

export default function ImageUpload({ bucket, currentUrl, onUpload, label = 'رفع صورة', folder = 'uploads' }: ImageUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<string>(currentUrl || '');
  const [error, setError] = useState('');

  const handleFile = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('يرجى اختيار ملف صورة صالح (JPG, PNG, WebP)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('حجم الصورة يجب أن يكون أقل من 5 ميجابايت');
      return;
    }
    setError('');
    setUploading(true);
    const localPreview = URL.createObjectURL(file);
    setPreview(localPreview);
    try {
      const ext = file.name.split('.').pop() || 'jpg';
      const path = `${folder}/${Date.now()}.${ext}`;
      const url = await uploadImage(bucket, file, path);
      setPreview(url);
      onUpload(url);
    } catch (err: any) {
      setError('فشل رفع الصورة. حاول مرة أخرى.');
      setPreview(currentUrl || '');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleClear = () => {
    setPreview('');
    onUpload('');
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="space-y-2">
      {label && <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</label>}
      {preview ? (
        <div className="relative w-full h-40 rounded-xl overflow-hidden border border-border group">
          <AppImage
            src={preview}
            alt="صورة مرفوعة"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="px-3 py-1.5 bg-accent text-accent-foreground rounded-lg text-xs font-semibold"
            >
              تغيير
            </button>
            <button
              type="button"
              onClick={handleClear}
              className="px-3 py-1.5 bg-danger text-danger-foreground rounded-lg text-xs font-semibold"
            >
              حذف
            </button>
          </div>
          {uploading && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
              <Loader2 size={24} className="text-white animate-spin" />
            </div>
          )}
        </div>
      ) : (
        <div
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => inputRef.current?.click()}
          className="w-full h-32 border-2 border-dashed border-border rounded-xl flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-accent/50 hover:bg-accent/5 transition-all duration-150"
        >
          {uploading ? (
            <Loader2 size={24} className="text-accent animate-spin" />
          ) : (
            <>
              <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
                <ImageIcon size={18} className="text-muted-foreground" />
              </div>
              <p className="text-xs text-muted-foreground text-center">
                <span className="text-accent font-semibold">اضغط للرفع</span> أو اسحب الصورة هنا
              </p>
              <p className="text-xs text-muted-foreground">JPG, PNG, WebP — حتى 5 ميجا</p>
            </>
          )}
        </div>
      )}
      {error && <p className="text-xs text-danger">{error}</p>}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
        }}
      />
    </div>
  );
}
